<template>
  <section class="reviews-section-full" v-if="reviews.length > 0">
    
    <div class="reviews-header">
      <h3>O que dizem nossos clientes ❤️</h3>
    </div>
    
    <div class="carousel-wrapper">
      <div class="cards-container">
        <div
          v-for="(review, index) in reviews"
          :key="review.id"
          ref="cardRefs"
          class="review-card"
          :class="{ 'is-active': index === activeIndex }"
          :data-index="index"
          @click="scrollToIndex(index)"
        >
          <div class="card-header">
            <span class="client-name">{{ review.nome_cliente }}</span>
            <span class="stars">
               <span v-for="n in 5" :key="n" class="material-icons-outlined star-icon">
                 {{ n <= review.nota ? 'star' : 'star_border' }}
               </span>
            </span>
          </div>
          <p class="comment">"{{ review.comentario }}"</p>
          <span class="date">{{ formatDate(review.created_at) }}</span>
        </div>
      </div>
    </div>
    
    <div class="reviews-footer" v-if="reviews.length > 1">
      <div class="carousel-indicators">
        <button
          v-for="(review, index) in reviews"
          :key="'dot-' + index"
          class="dot"
          :class="{ active: index === activeIndex }"
          @click="scrollToIndex(index)"
          :aria-label="'Ir para avaliação ' + (index + 1)"
        ></button>
      </div>
    </div>
  
  </section>
</template>

<script>
import api from '@/services/api';

export default {
  name: "AvaliacoesCarousel",
  data() {
    return {
      reviews: [],
      activeIndex: 0,
      observer: null
    };
  },
  watch: {
    reviews(newVal) {
      if (newVal && newVal.length > 0) {
        this.$nextTick(() => {
          this.inicializarCarrossel();
        });
      }
    }
  },
  async mounted() {
    try {
      const res = await api.get('/api/avaliacoes/publicas');
      this.reviews = res.data;
    } catch (e) {
      console.error("Erro ao carregar avaliações", e);
    }
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
  },
  methods: {
    formatDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString('pt-BR');
    },
    
    // Nova função para organizar a inicialização
    inicializarCarrossel() {
      // Centraliza o card do meio se houver muitos
      if (this.reviews.length > 2) {
        const middleIndex = Math.floor(this.reviews.length / 2);
        this.activeIndex = middleIndex;
        this.scrollToIndex(middleIndex);
      }
      
      // Inicia o observador com um pequeno delay para garantir estabilidade do DOM
      setTimeout(() => {
        this.setupObserver();
      }, 500);
    },
    
    setupObserver() {
      // Verificação de Segurança: Se o componente já foi destruído ou o elemento não existe
      if (!this.$el || typeof this.$el.querySelector !== 'function') return;
      
      const wrapper = this.$el.querySelector('.carousel-wrapper');
      if (!wrapper) return;
      
      // Limpa observador anterior se existir (para evitar duplicidade)
      if (this.observer) this.observer.disconnect();
      
      const options = {
        root: wrapper,
        rootMargin: '0px -50% 0px -50%',
        threshold: 0
      };
      
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.activeIndex = parseInt(entry.target.dataset.index);
          }
        });
      }, options);
      
      if (this.$refs.cardRefs) {
        this.$refs.cardRefs.forEach(card => this.observer.observe(card));
      }
    },
    
    scrollToIndex(index) {
      if (!this.$refs.cardRefs || !this.$refs.cardRefs[index]) return;
      
      const card = this.$refs.cardRefs[index];
      
      if (!this.$el || typeof this.$el.querySelector !== 'function') return;
      const wrapper = this.$el.querySelector('.carousel-wrapper');
      
      if (card && wrapper) {
        // Lógica de centralização matemática (Bulletproof)
        // Calcula onde o card está em relação ao container e ajusta o scroll
        const cardLeft = card.offsetLeft;
        const cardWidth = card.offsetWidth;
        const wrapperWidth = wrapper.offsetWidth;
        
        // Posição do card - Metade da tela + Metade do card
        const targetScroll = cardLeft - (wrapperWidth / 2) + (cardWidth / 2);
        
        wrapper.scrollTo({
          left: targetScroll,
          behavior: 'smooth'
        });
      }
    },
  }
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.reviews-section-full {
  width: 100%;
  background-color: var(--bg-main);
  border-top: 1px solid var(--border-light);
  display: flex;
  flex-direction: column;
  padding-top: 40px;
  padding-bottom: 30px;
}

.reviews-header, .reviews-footer {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  text-align: center;
}

.reviews-header h3 {
  font-size: 1.8rem;
  color: var(--text-hero);
  margin-bottom: 30px;
  margin-top: 0;
}

/* --- CARROSSEL --- */
.carousel-wrapper {
  width: 100%;
  overflow-x: auto;
  padding-bottom: 30px;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.carousel-wrapper::-webkit-scrollbar {
  display: none;
}

.cards-container {
  display: flex;
  gap: 15px; /* Espaço entre cards */
  width: max-content;
  padding-left: calc(50vw - 150px);
  padding-right: calc(50vw - 150px);
}

/* --- CARD --- */
.review-card {
  background-color: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  width: 300px;
  min-width: 300px;
  flex-shrink: 0;
  scroll-snap-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: all 0.3s ease;
  opacity: 0.5;
  transform: scale(0.9);
  filter: grayscale(0.4);
}

.review-card.is-active {
  opacity: 1;
  transform: scale(1);
  filter: grayscale(0);
  border-color: var(--color-accent);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  z-index: 2;
}

.card-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 15px;
}

.client-name {
  font-weight: 700;
  font-size: 1.1rem;
  color: var(--text-main);
  margin-bottom: 5px;
}

.stars {
  color: #FFD700;
  display: flex;
}

.star-icon {
  font-size: 18px;
}

.comment {
  font-style: italic;
  color: var(--text-secondary);
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 15px;
  flex: 1;
}

.date {
  font-size: 0.75rem;
  color: var(--text-secondary);
  opacity: 0.7;
}

/* --- INDICADORES --- */
.carousel-indicators {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 10px;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: var(--border-light);
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
}
.dot.active {
  background-color: var(--color-accent);
  transform: scale(1.3);
}

@media (max-width: 700px) {
  .cards-container {
    padding-left: 15vw;
    padding-right: 15vw;
  }
  
  .review-card {
    width: 70vw;
    min-width: 70vw;
  }
  
  .review-card.is-active {
    transform: scale(1);
  }
}
</style>